import { TeethSelect, validateSchema } from "@jasper/forms";
import { Stack } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { z } from "zod";

import type { ProductStepProps } from "../types";
import { useProductGroup } from "../../hooks/useProductGroup";
import { usePreselectedTeeth } from "../../hooks/usePreselectedTeeth";
import { bulkTeethSchema } from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import {
  useTeethSelectionSubmission,
  type FormatTeethSelection,
} from "./useTeethSelectionSubmission";
import { useRegisterStepSave } from "../useRegisterStepSave";
import { defaultApplyTeethSelection } from "./defaultApplyTeethSelection";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";

export type ToothStepProps = ProductStepProps & {
  applyTeethSelection?: FormatTeethSelection<number>;
};

const validateSelection = ({ value }: { value: { teeth: number[] } }) => {
  const result = validateSchema(z.object({ teeth: bulkTeethSchema }), value);

  return result.success ? undefined : result.errors;
};

export const ToothStep = ({
  stepKey,
  products,
  orderConfiguration,
  itemType,
  applyTeethSelection = defaultApplyTeethSelection,
  registerStepSave,
}: ToothStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const describeTeethSelection = useTeethSelectionDescription();

  const { groupTeeth } = useProductGroup(products);
  const preselectedTeeth = usePreselectedTeeth();

  const submitTeethSelection = useTeethSelectionSubmission({
    products,
    itemType,
    orderConfiguration,
    formatTeethSelection: applyTeethSelection,
  });

  const initialTeeth = preselectedTeeth.length ? preselectedTeeth : groupTeeth;

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...initialTeeth],
      },
    }),
    [initialTeeth]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      // One product per tooth.
      await submitTeethSelection(value.product.teeth.map(tooth => [tooth]));
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: number[];
    setSelectedTeeth: (teeth: number[]) => void;
  }) => {
    if (selectedTeeth.includes(toothId)) {
      setSelectedTeeth(selectedTeeth.filter(id => id !== toothId));
    } else {
      setSelectedTeeth([...selectedTeeth, toothId]);
    }
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <form.Field
        name="product"
        listeners={{
          onChange: ({ fieldApi }) => {
            fieldApi.setErrorMap({
              onSubmit: undefined,
              onBlur: undefined,
            });
          },
        }}
        validators={{
          onChange: ({ value, fieldApi }) => {
            const errors = validateSelection({ value });

            if (!errors) {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            }

            return errors;
          },
          onSubmit: validateSelection,
          onBlur: validateSelection,
        }}
      >
        {field => {
          // Never display onChange errors, only onBlur and onSubmit.
          // Showing errors on onChange is too aggressive and can be confusing to the user.
          const formErrors =
            field.state.meta.errorMap.onBlur ??
            field.state.meta.errorMap.onSubmit;

          const errors =
            typeof formErrors?.teeth === "string"
              ? t(formErrors?.teeth, {
                  keyPrefix: "steps.teeth.errors",
                  defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                })
              : undefined;

          return (
            <TeethSelect
              onBlur={() => field.handleBlur()}
              teeth={field.state.value.teeth}
              error={errors}
              teethError={errors ? field.state.value.teeth : undefined}
              onClick={toothId => {
                handleToothClick({
                  toothId,
                  selectedTeeth: field.state.value.teeth,
                  setSelectedTeeth: teeth =>
                    field.setValue({ ...field.state.value, teeth }),
                });
              }}
              getTeethDescription={({ teeth }) =>
                describeTeethSelection(teeth, [
                  { type: itemType, count: teeth.length },
                ])
              }
            />
          );
        }}
      </form.Field>
    </Stack>
  );
};
